import React, { ComponentProps, memo } from 'react'
import {
  Controller,
  ControllerProps,
  FieldErrors,
  get,
  FieldValues,
} from 'react-hook-form'

import { NumberInput } from '@surglogs-app/ui'

import { DefaultFormField, DefaultFormFieldProps } from './DefaultFormField'

interface NumberInputFieldProps<TValuesModel extends FieldValues>
  extends Omit<ComponentProps<typeof NumberInput>, 'name' | 'defaultValue'>,
    Pick<
      ControllerProps<TValuesModel>,
      'shouldUnregister' | 'control' | 'name' | 'defaultValue'
    > {
  errors?: FieldErrors<any>
  warnings?: Record<string, any>
  onChange?: (e: string) => void
  formFieldProps?: Omit<
    DefaultFormFieldProps,
    'name' | 'errors' | 'warnings' | 'children'
  >
}

const Component = <TValuesModel extends FieldValues>({
  control,
  defaultValue,
  errors,
  warnings,
  name,
  invalid,
  warning,
  shouldUnregister,
  onChange,
  ...rest
}: NumberInputFieldProps<TValuesModel>) => (
  <Controller<TValuesModel>
    {...{
      defaultValue,
      control,
      name,
      shouldUnregister,
      render: ({ field: { onBlur, onChange: onFieldChange, value, ref } }) => (
        <NumberInput
          {...rest}
          {...{
            ref,
            onBlur,
            onChange: (e) => {
              onFieldChange(e)
              onChange?.(e)
            },
            invalid: invalid ?? !!get(errors || {}, name),
            warning: invalid ? false : warning ?? !!get(warnings || {}, name),
            value,
          }}
        />
      ),
    }}
  />
)

export const NumberInputField = memo(
  <TValuesModel extends FieldValues = FieldValues>({
    formFieldProps = undefined,
    ...rest
  }: NumberInputFieldProps<TValuesModel>) => {
    if (formFieldProps) {
      return (
        <DefaultFormField
          {...{
            ...formFieldProps,
            name: rest.name,
            errors: rest.errors,
            warnings: rest.warnings,
            invalid: rest.invalid,
          }}>
          <Component {...rest} />
        </DefaultFormField>
      )
    }

    return <Component {...rest} />
  },
) as typeof Component
